"use client";

import { useMemo, useState, type RefObject } from "react";
import {
  ChevronRight,
  Circle,
  Copy,
  File,
  FileText,
  Folder,
  FolderOpen,
  Download,
  Loader2,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Settings,
  SquarePen,
  Trash2,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { PermissionGate } from "@/components/shared/permission-gate";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { CreateCommandProjectPopover } from "@/components/aios-command/chat/create-command-project-popover";
import { EditCommandProjectDialog } from "@/components/aios-command/chat/edit-command-project-dialog";
import { RenameCommandProjectDialog } from "@/components/aios-command/chat/rename-command-project-dialog";
import { useCommandProjects } from "@/hooks/use-command-projects";
import { useCommandProjectSessions } from "@/hooks/use-command-sessions";
import type { CommandProject, CommandSession } from "@/types/command-agent";
import { formatSessionSidebarTime } from "@/utils/command-session";
import { cn } from "@/lib/utils";

type CommandSessionExportFormat = "MARKDOWN" | "PDF" | "CLIPBOARD";

interface CommandSessionRowControls {
  onPinToggle: (session: CommandSession) => void;
  onStartRename: (session: CommandSession) => void;
  onExportSession?: (sessionId: string, format: CommandSessionExportFormat) => void;
  editingSessionId: string | null;
  editTitle: string;
  onEditTitleChange: (value: string) => void;
  onSaveEdit: (sessionId: string) => void;
  onEditKeyDown: (e: React.KeyboardEvent, sessionId: string) => void;
  originalTitle: string;
  editInputRef?: RefObject<HTMLInputElement | null>;
  isUpdatingSession?: boolean;
  isDeletingSession?: boolean;
  exportingSessionId?: string | null;
  typingSessionId?: string | null;
  displayedTitle?: string;
}

interface SessionActionsProps extends CommandSessionRowControls {
  activeSessionId: string | null;
  onSessionSelect: (sessionId: string, agentId?: string | null) => void;
  onNewChatInProject: (projectId: string) => void;
  onMoveSession: (sessionId: string, projectId: string | null, sessionAgentId?: string | null) => void;
  onDeleteSession: (sessionId: string, title?: string) => void;
  creatingSessionProjectId?: string | null;
}

interface CommandOtherChatsProps extends Omit<SessionActionsProps, "onNewChatInProject"> {
  unfiledSessions: CommandSession[];
  projects: CommandProject[];
}

function ProjectChatRow({
  session,
  active,
  projects,
  onSelect,
  onMove,
  onDelete,
  onPinToggle,
  onStartRename,
  onExportSession,
  editingSessionId,
  editTitle,
  onEditTitleChange,
  onSaveEdit,
  onEditKeyDown,
  originalTitle,
  editInputRef,
  isUpdatingSession,
  isDeletingSession,
  exportingSessionId,
  typingSessionId,
  displayedTitle,
}: {
  session: CommandSession;
  active: boolean;
  projects: CommandProject[];
  onSelect: () => void;
  onMove: (projectId: string | null) => void;
  onDelete: () => void;
} & CommandSessionRowControls) {
  const isEditing = editingSessionId === session.id;
  const timeLabel = formatSessionSidebarTime(
    session.updated_at || session.created_at
  );

  return (
    <div
      className={cn(
        "group flex min-w-0 max-w-full items-center gap-0.5 rounded-lg pl-2 pr-1",
        active
          ? "bg-violet-50 dark:bg-violet-950/30"
          : "hover:bg-muted/70"
      )}
    >
      {isEditing ? (
        <input
          ref={editInputRef}
          type="text"
          value={editTitle}
          onChange={(e) => onEditTitleChange(e.target.value)}
          onBlur={() => {
            setTimeout(() => {
              if (editingSessionId === session.id) {
                onSaveEdit(session.id);
              }
            }, 150);
          }}
          onKeyDown={(e) => onEditKeyDown(e, session.id)}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="my-1 min-w-0 flex-1 rounded-md border border-border bg-transparent px-2 py-1 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder={originalTitle || "Untitled"}
          autoFocus
        />
      ) : (
        <button
          type="button"
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-start gap-1.5 py-1.5 text-left"
        >
          <Circle
            className={cn(
              "mt-1 size-2 shrink-0 stroke-[1.5]",
              active ? "text-primary" : "text-muted-foreground/45"
            )}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
            {typingSessionId === session.id ? (
              <span
                className={cn(
                  "truncate text-[13px] leading-snug",
                  active ? "font-medium text-foreground" : "text-foreground/80"
                )}
              >
                {displayedTitle || "Untitled"}
                <span className="animate-pulse">|</span>
              </span>
            ) : (
              <span
                className={cn(
                  "truncate text-[13px] leading-snug",
                  active ? "font-medium text-foreground" : "text-foreground/80"
                )}
              >
                {session.title || "Untitled"}
              </span>
            )}
            {timeLabel ? (
              <span
                className={cn(
                  "text-[10px] tabular-nums leading-tight",
                  active
                    ? "text-violet-600/70 dark:text-violet-300/70"
                    : "text-muted-foreground/70"
                )}
              >
                {timeLabel}
              </span>
            ) : null}
          </span>
        </button>
      )}
      {!isEditing && session.pinned ? (
        <Pin className="size-3 shrink-0 text-muted-foreground/60" />
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            type="button"
            className="size-6 shrink-0 p-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="size-3.5 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <PermissionGate requiredPermissions={["UPDATE_COMMAND_SESSION"]}>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onPinToggle(session);
              }}
              disabled={isUpdatingSession || isEditing}
            >
              {session.pinned ? (
                <>
                  <PinOff className="mr-2 size-3.5" />
                  Unpin
                </>
              ) : (
                <>
                  <Pin className="mr-2 size-3.5" />
                  Pin
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onStartRename(session);
              }}
              disabled={isEditing}
            >
              <Pencil className="mr-2 size-3.5" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={isEditing}>
                <Folder className="mr-2 size-3.5" />
                Move to project…
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
                {projects.map((project) => (
                  <DropdownMenuItem
                    key={project.id}
                    disabled={session.project_id === project.id}
                    onClick={() => onMove(project.id)}
                  >
                    <Folder className="mr-2 size-3.5" />
                    {project.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={!session.project_id}
                  onClick={() => onMove(null)}
                >
                  Other chats
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </PermissionGate>
          {onExportSession ? (
            <PermissionGate requiredPermissions={["LIST_COMMAND_SESSIONS"]}>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  disabled={exportingSessionId === session.id || isEditing}
                >
                  <Download className="mr-2 size-3.5" />
                  Export
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-44">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onExportSession(session.id, "MARKDOWN");
                    }}
                    disabled={exportingSessionId === session.id}
                  >
                    <FileText className="mr-2 size-3.5" />
                    Markdown (.md)
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onExportSession(session.id, "PDF");
                    }}
                    disabled={exportingSessionId === session.id}
                  >
                    <File className="mr-2 size-3.5" />
                    PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onExportSession(session.id, "CLIPBOARD");
                    }}
                    disabled={exportingSessionId === session.id}
                  >
                    <Copy className="mr-2 size-3.5" />
                    Copy to Clipboard
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </PermissionGate>
          ) : null}
          <DropdownMenuSeparator />
          <PermissionGate requiredPermissions={["DELETE_COMMAND_SESSION"]}>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={onDelete}
              disabled={isDeletingSession || isEditing}
            >
              <Trash2 className="mr-2 size-3.5" />
              Delete
            </DropdownMenuItem>
          </PermissionGate>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function ProjectFolder({
  project,
  projects,
  onRename,
  onEdit,
  onDelete,
  activeSessionId,
  onSessionSelect,
  onNewChatInProject,
  onMoveSession,
  onDeleteSession,
  creatingSessionProjectId,
  onPinToggle,
  onStartRename,
  onExportSession,
  editingSessionId,
  editTitle,
  onEditTitleChange,
  onSaveEdit,
  onEditKeyDown,
  originalTitle,
  editInputRef,
  isUpdatingSession,
  isDeletingSession,
  exportingSessionId,
  typingSessionId,
  displayedTitle,
}: {
  project: CommandProject;
  projects: CommandProject[];
  onRename: (project: CommandProject) => void;
  onEdit: (project: CommandProject) => void;
  onDelete: (project: CommandProject) => void;
} & SessionActionsProps) {
  const { sessions, isLoadingSessions, hasNextPage, isFetchingNextPage, loadMoreRef } =
    useCommandProjectSessions(project.id);
  const isCreatingInProject = creatingSessionProjectId === project.id;

  return (
    <Collapsible defaultOpen className="group/proj min-w-0">
      <div className="group/projrow flex w-full min-w-0 max-w-full items-center gap-0.5 rounded-lg pl-1 pr-1 hover:bg-muted/70">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-label={`Toggle “${project.name}”`}
            className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronRight className="size-3.5 transition-transform group-data-[state=open]/proj:rotate-90" />
          </button>
        </CollapsibleTrigger>
        <div className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left">
          <Folder className="size-3.5 shrink-0 text-primary group-data-[state=open]/proj:hidden" />
          <FolderOpen className="hidden size-3.5 shrink-0 text-primary group-data-[state=open]/proj:block" />
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
            {project.name}
          </span>
        </div>
        <PermissionGate requiredPermissions={["CREATE_COMMAND_SESSION"]}>
          <button
            type="button"
            onClick={() => onNewChatInProject(project.id)}
            disabled={isCreatingInProject}
            title={
              isCreatingInProject
                ? "Creating chat…"
                : "New chat in this project"
            }
            aria-label={`New chat in “${project.name}”`}
            aria-busy={isCreatingInProject}
            className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover/projrow:opacity-100 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-40 group-hover/projrow:disabled:opacity-40"
          >
            {isCreatingInProject ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <SquarePen className="size-3.5" />
            )}
          </button>
        </PermissionGate>
        {project.is_owner ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                type="button"
                aria-label={`Project options for “${project.name}”`}
                className="size-6 shrink-0 p-0 opacity-0 group-hover/projrow:opacity-100 data-[state=open]:opacity-100"
              >
                <MoreHorizontal className="size-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <PermissionGate requiredPermissions={["UPDATE_COMMAND_PROJECT"]}>
                <DropdownMenuItem onClick={() => onEdit(project)}>
                  <Settings className="mr-2 size-3.5" />
                  Project settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRename(project)}>
                  <Pencil className="mr-2 size-3.5" />
                  Rename
                </DropdownMenuItem>
              </PermissionGate>
              <DropdownMenuSeparator />
              <PermissionGate requiredPermissions={["DELETE_COMMAND_PROJECT"]}>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(project)}
                >
                  <Trash2 className="mr-2 size-3.5" />
                  Delete project
                </DropdownMenuItem>
              </PermissionGate>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      <CollapsibleContent>
        <div className="ml-3 min-w-0 space-y-0.5 border-l border-border/60 pl-2 pt-0.5">
          {isLoadingSessions && sessions.length === 0 ? (
            <div className="space-y-1 px-1 py-1">
              <Skeleton className="h-6 w-full rounded-md" />
              <Skeleton className="h-6 w-full rounded-md" />
            </div>
          ) : null}
          {sessions.map((session) => (
            <ProjectChatRow
              key={session.id}
              session={session}
              active={session.id === activeSessionId}
              projects={projects}
              onSelect={() => {
                if (editingSessionId) return;
                onSessionSelect(session.id, session.agent_id);
              }}
              onMove={(projectId) => onMoveSession(session.id, projectId, session.agent_id)}
              onDelete={() => onDeleteSession(session.id, session.title || undefined)}
              onPinToggle={onPinToggle}
              onStartRename={onStartRename}
              onExportSession={onExportSession}
              editingSessionId={editingSessionId}
              editTitle={editTitle}
              onEditTitleChange={onEditTitleChange}
              onSaveEdit={onSaveEdit}
              onEditKeyDown={onEditKeyDown}
              originalTitle={originalTitle}
              editInputRef={editInputRef}
              isUpdatingSession={isUpdatingSession}
              isDeletingSession={isDeletingSession}
              exportingSessionId={exportingSessionId}
              typingSessionId={typingSessionId}
              displayedTitle={displayedTitle}
            />
          ))}
          {!isLoadingSessions && sessions.length === 0 ? (
            <p className="px-2 py-1.5 text-[11px] text-muted-foreground">
              Empty — start a chat here
            </p>
          ) : null}
          {hasNextPage ? (
            <div ref={loadMoreRef} className="h-1">
              {isFetchingNextPage ? (
                <Skeleton className="mx-1 my-1 h-6 w-full rounded-md" />
              ) : null}
            </div>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Pinned above the folders block — same pattern as “My Agents”. */
export function CommandProjectsHeader() {
  const { createProject, isCreatingProject, canListProjects } = useCommandProjects();

  if (!canListProjects) return null;

  return (
    <div className="flex items-center justify-between px-0 pb-1 pt-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Projects
      </p>
      <CreateCommandProjectPopover
        onCreate={({ name, visibility }) => {
          createProject({ name, visibility });
        }}
        isCreating={isCreatingProject}
      />
    </div>
  );
}

/** Project folders only — pinned above Other chats (does not scroll away). */
export function CommandProjectsFolders(props: SessionActionsProps) {
  const personal = useCommandProjects({ visibility: "PERSONAL" });
  const team = useCommandProjects({ visibility: "TEAM" });
  const {
    updateProject,
    isUpdatingProject,
    deleteProject,
    isDeletingProject,
  } = useCommandProjects();

  const [editTarget, setEditTarget] = useState<CommandProject | null>(null);
  const [renameTarget, setRenameTarget] = useState<CommandProject | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CommandProject | null>(null);

  const allProjects = useMemo(() => {
    const merged = [...personal.allProjects, ...team.allProjects];
    const seen = new Set<string>();
    return merged.filter((project) => {
      if (seen.has(project.id)) return false;
      seen.add(project.id);
      return true;
    });
  }, [personal.allProjects, team.allProjects]);

  const canListProjects = personal.canListProjects || team.canListProjects;
  // Only block the list on the first load — refetch after create must not flash skeletons.
  const isLoadingProjects =
    (personal.isLoadingProjects && personal.allProjects.length === 0) ||
    (team.isLoadingProjects && team.allProjects.length === 0 && personal.allProjects.length === 0);

  if (!canListProjects) return null;

  return (
    <div className="min-w-0 max-w-full overflow-x-hidden">
      {isLoadingProjects ? (
        <div className="space-y-1">
          <Skeleton className="h-7 w-full rounded-lg" />
          <Skeleton className="h-7 w-full rounded-lg" />
        </div>
      ) : allProjects.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-3 py-4 text-center">
          <div className="flex size-8 items-center justify-center rounded-full bg-muted">
            <Folder className="size-3.5 text-muted-foreground" />
          </div>
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-foreground">No projects yet</p>
            <p className="text-[11px] text-muted-foreground">
              Create one to group related chats.
            </p>
          </div>
        </div>
      ) : (
        <div className="min-w-0 space-y-0.5">
          {allProjects.map((project) => (
            <ProjectFolder
              key={project.id}
              project={project}
              projects={allProjects}
              onRename={setRenameTarget}
              onEdit={setEditTarget}
              onDelete={setDeleteTarget}
              {...props}
            />
          ))}
        </div>
      )}

      <RenameCommandProjectDialog
        project={renameTarget}
        open={!!renameTarget}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
        isPending={isUpdatingProject}
        onRename={(projectId, name) => {
          updateProject(
            { projectId, payload: { name } },
            { onSuccess: () => setRenameTarget(null) }
          );
        }}
      />

      <EditCommandProjectDialog
        project={editTarget}
        open={!!editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
        isPending={isUpdatingProject}
        onSave={(projectId, payload) => {
          updateProject(
            { projectId, payload },
            { onSuccess: () => setEditTarget(null) }
          );
        }}
      />

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteProject(deleteTarget.id, {
            onSuccess: () => setDeleteTarget(null),
          });
        }}
        itemName={deleteTarget?.name || "project"}
        itemType="project"
        isLoading={isDeletingProject}
      />
    </div>
  );
}

/** Pinned above the Other chats list — same pattern as Projects / My Agents. */
export function CommandOtherChatsHeader() {
  return (
    <p className="px-0.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      Other chats
    </p>
  );
}

/** Unfiled sessions — scrolls under the pinned Other chats label. */
export function CommandOtherChats({
  unfiledSessions,
  projects,
  showHeader = true,
  activeSessionId,
  onSessionSelect,
  onMoveSession,
  onDeleteSession,
  onPinToggle,
  onStartRename,
  onExportSession,
  editingSessionId,
  editTitle,
  onEditTitleChange,
  onSaveEdit,
  onEditKeyDown,
  originalTitle,
  editInputRef,
  isUpdatingSession,
  isDeletingSession,
  exportingSessionId,
  typingSessionId,
  displayedTitle,
}: CommandOtherChatsProps & { showHeader?: boolean }) {
  return (
    <div className="min-w-0 max-w-full overflow-x-hidden">
      {showHeader ? <CommandOtherChatsHeader /> : null}
      <div className="min-w-0 space-y-0.5">
        {unfiledSessions.map((session) => (
          <ProjectChatRow
            key={session.id}
            session={session}
            active={session.id === activeSessionId}
            projects={projects}
            onSelect={() => {
              if (editingSessionId) return;
              onSessionSelect(session.id, session.agent_id);
            }}
            onMove={(projectId) => onMoveSession(session.id, projectId, session.agent_id)}
            onDelete={() => onDeleteSession(session.id, session.title || undefined)}
            onPinToggle={onPinToggle}
            onStartRename={onStartRename}
            onExportSession={onExportSession}
            editingSessionId={editingSessionId}
            editTitle={editTitle}
            onEditTitleChange={onEditTitleChange}
            onSaveEdit={onSaveEdit}
            onEditKeyDown={onEditKeyDown}
            originalTitle={originalTitle}
            editInputRef={editInputRef}
            isUpdatingSession={isUpdatingSession}
            isDeletingSession={isDeletingSession}
            exportingSessionId={exportingSessionId}
            typingSessionId={typingSessionId}
            displayedTitle={displayedTitle}
          />
        ))}
        {unfiledSessions.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-3 py-5 text-center">
            <div className="flex size-8 items-center justify-center rounded-full bg-muted">
              <MessageSquare className="size-3.5 text-muted-foreground" />
            </div>
            <div className="space-y-0.5">
              <p className="text-xs font-medium text-foreground">No other chats</p>
              <p className="text-[11px] text-muted-foreground">
                New chats land here until you move them into a project.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function CommandProjectsSection({
  unfiledSessions,
  ...sessionActions
}: SessionActionsProps & { unfiledSessions: CommandSession[] }) {
  const { projects } = useAllCommandProjectsForMove();

  return (
    <div className="min-w-0 space-y-1">
      <CommandProjectsFolders {...sessionActions} />
      <CommandOtherChats
        unfiledSessions={unfiledSessions}
        projects={projects}
        {...sessionActions}
      />
    </div>
  );
}

export function useAllCommandProjectsForMove() {
  const personal = useCommandProjects({ visibility: "PERSONAL" });
  const team = useCommandProjects({ visibility: "TEAM" });

  const projects = useMemo(() => {
    const merged = [...personal.allProjects, ...team.allProjects];
    const seen = new Set<string>();
    return merged.filter((project) => {
      if (seen.has(project.id)) return false;
      seen.add(project.id);
      return true;
    });
  }, [personal.allProjects, team.allProjects]);

  return {
    projects,
    canListProjects: personal.canListProjects || team.canListProjects,
  };
}

export function notifySessionMoved(
  projectId: string | null,
  projects: CommandProject[]
) {
  if (!projectId) {
    toast.success("Moved to Other chats");
    return;
  }
  const name = projects.find((project) => project.id === projectId)?.name;
  toast.success(name ? `Moved to “${name}”.` : "Moved to project");
}
